"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import { generateKundali } from "@/lib/api/kundali";
import type { KundaliRequest, KundaliResponse, DoshaResult } from "@/lib/api/kundali";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import OfferImpression from "@/components/OfferImpression";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { reportOfferCopyFor } from "@/lib/report-offer-copy";
import { buildUpayReveal, findDoshaForReveal, upayUnlockCtaLabel } from "@/lib/upay-reveal";
import RatingPrompt from "@/components/RatingPrompt";
import Explainable from "@ishvaram/ui/Explainable";
import { useExplainableHandlers, useExplainer } from "@/hooks/useExplainer";

const PaidReportCTA = dynamic(() => import("@/components/PaidReportCTA"), {
  ssr: false,
  loading: () => null,
});

function findPitraDosh(doshas: DoshaResult[]): DoshaResult | null {
  return (
    doshas.find((d) => d.dosha_id === "pitra_dosh" || d.name_en.toLowerCase().includes("pitra")) ??
    null
  );
}

/**
 * Renders the triggering planet as an Explainable (type="planet") — users
 * who tap "Rahu" get cultural context on why Rahu triggers Pitra Dosh.
 * Extracted as a component because hooks cannot be called in conditional JSX.
 */
function TriggeringPlanetField({ planet, house }: { planet: string; house: number }) {
  const id = planet.toLowerCase();
  const handlers = useExplainableHandlers("pitra_dosh_result");
  const { data, isLoading, error, refetch } = useExplainer("planet", id, "medium", {
    enabled: false,
  });
  return (
    <p className="text-[14px]" style={{ color: "var(--ish-dark)" }}>
      <Explainable
        type="planet"
        id={id}
        intensity="medium"
        triggerLabel={planet.charAt(0).toUpperCase() + planet.slice(1)}
        data={data}
        loading={isLoading}
        error={error ?? null}
        onRequestData={() => void refetch()}
        {...handlers}
      />{" "}
      {/* inert-by-design: house number gives spatial context for the planet's placement; planet above is Explainable */}
      in House {house}
    </p>
  );
}

function PitraDoshHeader() {
  return (
    <div className="text-center mb-10">
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3"
        style={{ color: "var(--ish-accent)" }}
      >
        Pitra Dosh Pariksha
      </p>
      <h1
        className="text-[28px] sm:text-[36px] font-bold leading-tight"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Free Pitra Dosh Calculator
      </h1>
      <p
        className="mt-3 text-[15px] leading-relaxed max-w-md mx-auto"
        style={{ color: "var(--ish-text-muted)" }}
      >
        Enter your birth details and check for Pitra Dosh (ancestral karmic debt) in your chart.
        Instant Sun/node affliction analysis, 9th house check, and Vedic upay — 100% free.
      </p>
      <div
        className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium"
        style={{ color: "var(--ish-text-faint)" }}
      >
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          Vedic Accuracy
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          Instant Results
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          100% Free
        </span>
      </div>
    </div>
  );
}

function PitraDoshDetectedCard({ pitraDosh }: { pitraDosh: DoshaResult }) {
  return (
    <div
      className="card-sacred p-6 sm:p-8"
      style={{
        borderColor: "rgba(146, 64, 14, 0.3)",
        backgroundColor: "rgba(255, 251, 235, 0.5)",
      }}
    >
      <div className="text-center mb-5">
        <span
          className="inline-flex items-center justify-center w-14 h-14 rounded-full text-2xl mb-3"
          style={{ backgroundColor: "rgba(146, 64, 14, 0.1)" }}
        >
          &#x2609;
        </span>
        <h2
          className="text-[24px] font-bold"
          style={{
            fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
            color: "var(--ish-dark)",
          }}
        >
          Pitra Dosh Detected
        </h2>
        {/* inert-by-design: severity percentage is a derived summary metric — the dosha is fully explained by the Explainable planet field below */}
        <p className="text-[14px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
          Severity: {Math.round(pitraDosh.severity * 100)}%
        </p>
      </div>

      <div className="divider-warm my-5" />

      <div className="space-y-4">
        {pitraDosh.triggering_planet && (
          <div>
            <span
              className="block text-[12px] font-semibold uppercase tracking-wider mb-1"
              style={{ color: "var(--ish-text-faint)" }}
            >
              Afflicting Planet
            </span>
            <TriggeringPlanetField
              planet={pitraDosh.triggering_planet}
              house={pitraDosh.triggering_house ?? 0}
            />
          </div>
        )}

        {pitraDosh.description && (
          <div>
            <span
              className="block text-[12px] font-semibold uppercase tracking-wider mb-1"
              style={{ color: "var(--ish-text-faint)" }}
            >
              Why This Dosh Exists
            </span>
            <p className="text-[14px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
              {pitraDosh.description}
            </p>
          </div>
        )}

        {pitraDosh.remedies && pitraDosh.remedies.length > 0 && (
          <div>
            <span
              className="block text-[12px] font-semibold uppercase tracking-wider mb-2"
              style={{ color: "var(--ish-text-faint)" }}
            >
              Recommended Upay
            </span>
            <div className="space-y-2">
              {pitraDosh.remedies.map((remedy, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 p-3 rounded-lg"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    border: "1px solid var(--ish-border)",
                  }}
                >
                  {/* inert-by-design: remedy category label (e.g. "tarpan", "puja") — not a Vedic concept requiring drilldown; the remedy text below explains the action */}
                  <span
                    className="text-[12px] font-semibold px-2 py-0.5 rounded"
                    style={{
                      backgroundColor: "var(--ish-accent-light)",
                      color: "var(--ish-accent)",
                    }}
                  >
                    {remedy.remedy_type}
                  </span>
                  <span className="text-[13px]" style={{ color: "var(--ish-text-muted)" }}>
                    {remedy.reason}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NoPitraDoshCard() {
  return (
    <div
      className="card-sacred p-6 sm:p-8 text-center"
      style={{
        borderColor: "rgba(22, 163, 74, 0.3)",
        backgroundColor: "rgba(240, 253, 244, 0.5)",
      }}
    >
      <span
        className="inline-flex items-center justify-center w-14 h-14 rounded-full text-2xl mb-3"
        style={{ backgroundColor: "rgba(22, 163, 74, 0.1)" }}
      >
        &#x2713;
      </span>
      <h2
        className="text-[24px] font-bold"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--color-success-text-dark)",
        }}
      >
        No Pitra Dosh
      </h2>
      <p className="text-[14px] mt-2 max-w-sm mx-auto" style={{ color: "var(--ish-text-muted)" }}>
        No Pitra Dosh was detected in your birth chart. The Sun is not afflicted by Rahu, Ketu, or
        Saturn, and the 9th house carries no malefic influence.
      </p>
    </div>
  );
}

function PitraDoshReportCTA({
  result,
  isPresent,
  defaultLanguage,
}: {
  result: KundaliResponse;
  isPresent: boolean;
  defaultLanguage: "en" | "hi";
}) {
  const rawBirth = result.birth_details as {
    name?: string;
    birth_date?: string;
    birth_place?: string;
    birth_time?: string | null;
  } | null;
  const birth = rawBirth?.name && rawBirth?.birth_date && rawBirth?.birth_place ? rawBirth : null;
  const hi = defaultLanguage === "hi";
  const sourcePath = hi ? "/hi/pitra-dosh-calculator" : "/pitra-dosh-calculator";
  const placement = "pitra_dosh_result_primary";
  if (!birth) return null;
  const planCopy = reportOfferCopyFor("pitra_dosh", defaultLanguage);
  const subhead = pitraReportSubhead(defaultLanguage);
  // Lead with the chart's actual Pitra Dosh (pain question + one free upay) when
  // present; otherwise keep the generic report headline (relevance lever).
  const reveal = buildUpayReveal(findDoshaForReveal(result.doshas, "pitra"), defaultLanguage);
  const headline = reveal ? reveal.painHeadline : pitraReportHeadline(isPresent, defaultLanguage);
  return (
    <>
      <OfferImpression
        planId="pitra_dosh"
        sourcePath={sourcePath}
        intent="pitra_dosh_report"
        placement={placement}
        customerProblem="pitra_dosh"
        urgency={isPresent ? "high" : "medium"}
        confidence={0.9}
        routeRankScore={90}
        routeRankReason="pitra_dosh_result"
      />
      <PaidReportCTA
        planId="pitra_dosh"
        priceInr={planCopy.priceInr}
        headline={headline}
        subhead={subhead}
        doshaReveal={
          reveal
            ? {
                freeUpayTitle: reveal.freeUpayTitle,
                freeUpayWhy: reveal.freeUpayWhy,
                ctaLabel: upayUnlockCtaLabel(planCopy.priceInr, defaultLanguage),
              }
            : undefined
        }
        language={defaultLanguage}
        source={hi ? "hi-pitra-dosh-calculator" : "pitra-dosh-calculator"}
        analyticsContext={{
          placement,
          checkoutChannel: "ishvaram_report",
          commerceSystem: "ishvaram",
          intent: "pitra_dosh_report",
          sourcePath,
          customerProblem: "pitra_dosh",
          urgency: isPresent ? "high" : "medium",
          confidence: 0.9,
          routeRankScore: 90,
          routeRankReason: "pitra_dosh_result",
          rankedOfferOrder: ["pitra_dosh"],
        }}
        reportInputs={{
          name: birth.name,
          birth_date: birth.birth_date,
          birth_time: birth.birth_time || null,
          birth_place: birth.birth_place,
          language: defaultLanguage,
        }}
      />
    </>
  );
}

export default function PitraDoshClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KundaliResponse | null>(null);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(null);
    try {
      const response = await generateKundali({ ...data, skip_interpretation: true });
      setResult(response);
      trackCalculatorUsed({ calculator: "pitra-dosh-calculator" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  const pitraDosh = result ? findPitraDosh(result.doshas) : null;
  const isPresent = result !== null && pitraDosh !== null && pitraDosh.is_present;

  return (
    <div className="intent-interface-diagnostic-first sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <PitraDoshHeader />

        {!result && (
          <div className="mx-auto max-w-md">
            <BirthDetailsForm
              onSubmit={handleSubmit}
              loading={loading}
              submitLabel={
                defaultLanguage === "hi" ? "Pitra Dosh check karein" : "Check Pitra Dosh"
              }
              error={error ?? undefined}
              language={defaultLanguage}
              analyticsToolName="pitra-dosh-calculator"
              showTrustStrip
            />
          </div>
        )}

        {result && (
          <div className="space-y-6">
            {isPresent && pitraDosh ? (
              <PitraDoshDetectedCard pitraDosh={pitraDosh} />
            ) : (
              <NoPitraDoshCard />
            )}

            <div style={{ marginTop: 24 }}>
              <RatingPrompt
                source={"tool_result" as never}
                mode="thumbs+text"
                prompt="Was this Pitra Dosh check accurate?"
                context={{
                  surface: "pitra_dosh_result",
                  path: typeof window !== "undefined" ? window.location.pathname : null,
                }}
              />
            </div>

            <PitraDoshReportCTA
              result={result}
              isPresent={isPresent}
              defaultLanguage={defaultLanguage}
            />

            <div className="flex justify-center">
              <button
                onClick={() => {
                  setResult(null);
                  setError(null);
                }}
                className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200"
                style={{
                  color: "var(--ish-text-muted)",
                  border: "1px solid var(--ish-border)",
                }}
              >
                Check Another Chart
              </button>
            </div>
          </div>
        )}

        <CalculatorCrossLinks currentPage="pitra-dosh-calculator" />
      </div>
    </div>
  );
}

function pitraReportHeadline(isPresent: boolean, language: "en" | "hi"): string {
  if (language === "hi") {
    return isPresent
      ? "Pitra Dosh Report - severity aur upay confirm karein"
      : "Pitra Dosh Report - cancellation aur ancestor pattern confirm karein";
  }
  return isPresent
    ? "Get your Pitra Dosh Report — severity, cancellation, and upay"
    : "Get your Pitra Dosh Report — confirm cancellation and ancestor patterns";
}

function pitraReportSubhead(language: "en" | "hi"): string {
  if (language === "hi") {
    return "Private PDF + web reading - 9th house, Surya, Rahu/Ketu, cancellation, dasha activation, tarpan/shradh guidance aur safe ancestral upay.";
  }
  return "Private PDF + web reading — 9th house, Sun, Rahu/Ketu, cancellation factors, dasha activation, tarpan/shradh guidance, and safe ancestral upay.";
}
